import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from '../components/CustomToast';

const LiveCallContext = createContext(null);

export const useLiveCall = () => {
  const context = useContext(LiveCallContext);
  if (!context) {
    throw new Error('useLiveCall must be used within a LiveCallProvider');
  }
  return context;
};

// --- Helpers
function parseSseEventBlock(rawBlock) {
  const lines = String(rawBlock || '').split(/\r?\n/);
  let event = 'message';
  const dataLines = [];

  lines.forEach((rawLine) => {
    const line = String(rawLine || '');
    if (!line || line.startsWith(':')) return;
    if (line.startsWith('event:')) {
      event = line.slice(6).trim() || 'message';
      return;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  });

  if (dataLines.length === 0) return null;
  return {
    event,
    data: dataLines.join('\n'),
  };
}

function consumeSseBuffer(buffer, onEvent) {
  const blocks = String(buffer || '').split(/\r?\n\r?\n/);
  const remainder = blocks.pop() ?? '';

  blocks.forEach((block) => {
    const parsedEvent = parseSseEventBlock(block);
    if (parsedEvent) {
      onEvent(parsedEvent);
    }
  });

  return remainder;
}

export const LiveCallProvider = ({ children }) => {
  const { token } = useAuth();
  
  const [liveTranscriptTarget, setLiveTranscriptTarget] = useState(null);
  const [liveTranscript, setLiveTranscript] = useState(null);
  const [liveTranscriptLoading, setLiveTranscriptLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const startLiveTranscript = useCallback((targetData) => {
    setLiveTranscript(null);
    setLiveTranscriptTarget(targetData);
    setIsMinimized(false);
  }, []);

  const updateLiveTranscriptTarget = useCallback((patch) => {
    setLiveTranscriptTarget((currentTarget) => {
      if (!currentTarget) return currentTarget;
      return { ...currentTarget, ...patch };
    });
  }, []);

  const closeLiveTranscript = useCallback(() => {
    setLiveTranscriptTarget(null);
    setLiveTranscript(null);
    setLiveTranscriptLoading(false);
    setIsMinimized(false);
  }, []);

  const minimizeLiveTranscript = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const expandLiveTranscript = useCallback(() => {
    setIsMinimized(false);
  }, []);

  useEffect(() => {
    if (!token) {
      setLiveTranscriptTarget(null);
      setLiveTranscript(null);
      setLiveTranscriptLoading(false);
      setIsMinimized(false);
    }
  }, [token]);

    const fetchLiveTranscript = useCallback(async (callId, targetData = null, { silent = false } = {}) => {
      if (!callId) return { status: 'missing', payload: null };

      try {
        if (!silent) setLiveTranscriptLoading(true);
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const defaultPath = `/api/v1/test-call/${callId}/live-transcript`;
        const path = targetData?.fallbackPath || defaultPath;
        const res = await axios.get(`${apiUrl}${path}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      setLiveTranscript(res.data || null);
      return { status: 'found', payload: res.data || null };
    } catch (err) {
      if (err?.response?.status === 404) {
        setLiveTranscript(null);
        return { status: 'missing', payload: null };
      }
      if (!silent) toast.error('Failed to load live transcript');
      return { status: 'error', payload: null };
    } finally {
      if (!silent) setLiveTranscriptLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!liveTranscriptTarget?.call_id) {
      setLiveTranscript(null);
      setLiveTranscriptLoading(false);
      return undefined;
    }

    let isClosed = false;
    let hasEnded = false;
    let hasSeenTranscript = false;
    let fallbackIntervalId = null;
    const controller = new AbortController();
    const decoder = new TextDecoder();
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const { call_id: callId } = liveTranscriptTarget;

    const stopFallbackPolling = () => {
      if (fallbackIntervalId !== null) {
        window.clearInterval(fallbackIntervalId);
        fallbackIntervalId = null;
      }
    };

    const handleStreamEnded = () => {
      if (isClosed || hasEnded) return;
      hasEnded = true;
      stopFallbackPolling();
      setLiveTranscript(null);
      setLiveTranscriptLoading(false);
      setLiveTranscriptTarget(null);
      setIsMinimized(false);
      toast.success('Live transcript stopped because the call ended.');
    };

    const applyTranscriptPayload = (payload) => {
      if (isClosed || !payload || typeof payload !== 'object') return;
      hasSeenTranscript = true;
      setLiveTranscript(payload);
      setLiveTranscriptLoading(false);
    };

    const startFallbackPolling = async () => {
      if (isClosed || hasEnded || fallbackIntervalId !== null) return;
      const initialResult = await fetchLiveTranscript(callId, liveTranscriptTarget);
      if (initialResult?.status === 'found') {
        hasSeenTranscript = true;
      }
      if (isClosed || hasEnded) return;

      fallbackIntervalId = window.setInterval(async () => {
        const nextResult = await fetchLiveTranscript(callId, liveTranscriptTarget, { silent: true });
        if (nextResult?.status === 'found') {
          hasSeenTranscript = true;
          return;
        }
        if (nextResult?.status === 'missing' && hasSeenTranscript) {
          handleStreamEnded();
        }
      }, 2000);
    };

    const handleSseEvent = (parsedEvent) => {
      if (!parsedEvent?.data) return;

      let payload = null;
      try {
        payload = JSON.parse(parsedEvent.data);
      } catch (error) {
        return;
      }

      if (parsedEvent.event === 'ended') {
        handleStreamEnded();
        return;
      }

      applyTranscriptPayload(payload);
    };

    const connectLiveTranscriptStream = async () => {
      setLiveTranscriptLoading(true);
      let buffer = '';

      try {
        const defaultPath = `/api/v1/test-call/${callId}/live-transcript/stream`;
        const path = liveTranscriptTarget.endpointPath || defaultPath;
        const response = await fetch(
          `${apiUrl}${path}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'text/event-stream',
            },
            signal: controller.signal,
            cache: 'no-store',
          }
        );

        if (response.status === 404) {
          await startFallbackPolling();
          return;
        }

        if (!response.ok || !response.body) {
          throw new Error(`SSE stream request failed with status ${response.status}`);
        }

        const reader = response.body.getReader();

        while (!isClosed) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          buffer = consumeSseBuffer(buffer, handleSseEvent);
        }

        buffer += decoder.decode();
        buffer = consumeSseBuffer(buffer, handleSseEvent);

        if (!isClosed && !hasEnded) {
          await startFallbackPolling();
        }
      } catch (error) {
        if (controller.signal.aborted || isClosed || hasEnded) {
          return;
        }
        await startFallbackPolling();
      }
    };

    void connectLiveTranscriptStream();

    return () => {
      isClosed = true;
      controller.abort();
      stopFallbackPolling();
    };
  }, [fetchLiveTranscript, liveTranscriptTarget, token]);

  const value = {
    liveTranscriptTarget,
    liveTranscript,
    liveTranscriptLoading,
    isMinimized,
    startLiveTranscript,
    updateLiveTranscriptTarget,
    closeLiveTranscript,
    minimizeLiveTranscript,
    expandLiveTranscript,
  };

  return (
    <LiveCallContext.Provider value={value}>
      {children}
    </LiveCallContext.Provider>
  );
};
